import { useState, useEffect, useCallback, useRef } from 'react';
import { guestApiClient } from '../services/apiClient';
import { getTimeUntilExpiry, loadToken } from '../utils/tokenUtils';

interface TokenStatus {
  isValid: boolean;
  isExpired: boolean;
  isExpiringSoon: boolean;
  timeUntilExpiry: string;
  expiresAt: Date | null;
}

interface UseTokenManagerReturn {
  tokenStatus: TokenStatus;
  refreshToken: () => Promise<boolean>;
  isRefreshing: boolean;
  showWarning: boolean;
  extendSession: () => void;
  dismissWarning: () => void;
}

/**
 * Hook for managing JWT token lifecycle and user warnings
 */
export const useTokenManager = (): UseTokenManagerReturn => {
  const [tokenStatus, setTokenStatus] = useState<TokenStatus>({
    isValid: false,
    isExpired: false,
    isExpiringSoon: false,
    timeUntilExpiry: '',
    expiresAt: null,
  });
  
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);
  
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const warningShownRef = useRef(false);

  // Update token status
  const updateTokenStatus = useCallback(() => {
    const tokenInfo = guestApiClient.getTokenInfo();
    
    if (!tokenInfo) {
      setTokenStatus({
        isValid: false,
        isExpired: true,
        isExpiringSoon: true,
        timeUntilExpiry: 'No token',
        expiresAt: null,
      });
      return;
    }

    const timeUntilExpiry = getTimeUntilExpiry(guestApiClient.isAuthenticated() ? 
      loadToken('guest_token') || '' : '');

    setTokenStatus({
      isValid: !tokenInfo.isExpired,
      isExpired: tokenInfo.isExpired,
      isExpiringSoon: tokenInfo.isExpiringSoon,
      timeUntilExpiry,
      expiresAt: tokenInfo.expiresAt,
    });

    // Show warning if token is expiring soon (within 10 minutes)
    if (tokenInfo.isExpiringSoon && !warningShownRef.current && !warningDismissed) {
      const minutesLeft = Math.floor(tokenInfo.timeUntilExpiry / (1000 * 60));
      if (minutesLeft <= 10 && minutesLeft > 0) {
        setShowWarning(true);
        warningShownRef.current = true;
      }
    }
  }, [warningDismissed]);

  // Refresh token manually
  const refreshToken = useCallback(async (): Promise<boolean> => {
    if (isRefreshing) {
      return false;
    }

    setIsRefreshing(true);
    try {
      console.log('Manual token refresh initiated');
      const newToken = await (guestApiClient as any).refreshToken();
      
      if (newToken) {
        console.log('Token refresh successful');
        updateTokenStatus();
        setShowWarning(false);
        setWarningDismissed(false);
        warningShownRef.current = false;
        return true;
      }
      return false;
    } catch (error) {
      console.error('Manual token refresh failed:', error);
      return false;
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing, updateTokenStatus]);

  // Extend session (refresh token and dismiss warning)
  const extendSession = useCallback(async () => {
    const success = await refreshToken();
    if (success) {
      setShowWarning(false);
      setWarningDismissed(false);
      warningShownRef.current = false;
    }
  }, [refreshToken]);

  // Dismiss warning (but don't refresh token)
  const dismissWarning = useCallback(() => {
    setShowWarning(false);
    setWarningDismissed(true);
  }, []);

  // Set up polling for token status
  useEffect(() => {
    // Initial status update
    updateTokenStatus();

    // Update every 30 seconds
    intervalRef.current = setInterval(updateTokenStatus, 30000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [updateTokenStatus]);

  // Reset warning state when token changes
  useEffect(() => {
    if (tokenStatus.isValid && !tokenStatus.isExpiringSoon) {
      setShowWarning(false);
      setWarningDismissed(false);
      warningShownRef.current = false;
    }
  }, [tokenStatus.isValid, tokenStatus.isExpiringSoon]);

  return {
    tokenStatus,
    refreshToken,
    isRefreshing,
    showWarning,
    extendSession,
    dismissWarning,
  };
};

/**
 * Hook for simple token validation (lightweight version)
 */
export const useTokenValidation = () => {
  const [isValid, setIsValid] = useState(false);
  
  useEffect(() => {
    const checkToken = () => {
      setIsValid(guestApiClient.isTokenValid());
    };

    checkToken();
    const interval = setInterval(checkToken, 60000); // Check every minute

    return () => clearInterval(interval);
  }, []);

  return isValid;
};

/**
 * Hook for token expiry countdown
 */
export const useTokenCountdown = () => {
  const [countdown, setCountdown] = useState<string>('');
  const [isExpiringSoon, setIsExpiringSoon] = useState(false);

  useEffect(() => {
    const updateCountdown = () => {
      const tokenInfo = guestApiClient.getTokenInfo();
      
      if (!tokenInfo) {
        setCountdown('No token');
        setIsExpiringSoon(true);
        return;
      }

      if (tokenInfo.isExpired) {
        setCountdown('Expired');
        setIsExpiringSoon(true);
        return;
      }

      const minutes = Math.floor(tokenInfo.timeUntilExpiry / (1000 * 60));
      const seconds = Math.floor((tokenInfo.timeUntilExpiry % (1000 * 60)) / 1000);
      
      if (minutes > 60) {
        const hours = Math.floor(minutes / 60);
        setCountdown(`${hours}h ${minutes % 60}m`);
        setIsExpiringSoon(false);
      } else if (minutes > 0) {
        setCountdown(`${minutes}m ${seconds}s`);
        setIsExpiringSoon(minutes <= 10);
      } else {
        setCountdown(`${seconds}s`);
        setIsExpiringSoon(true);
      }
    };

    updateCountdown();
    const interval = setInterval(updateCountdown, 1000); // Update every second

    return () => clearInterval(interval);
  }, []);

  return { countdown, isExpiringSoon };
};